import { uploadJobFilesByID } from "actions/job";
import RightPanelCard from "components/Common/RightPanelCard";
import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { WORK_ORDER, MAX_FILE_SIZE, MAX_VIDEO_SIZE, ALLOWED_IMG_TYPES, ALLOWED_VIDEO_TYPES } from "components/Common/constants";
import { convertHeicIfNeeded } from "components/Common/commonFunctions";

// Below function is used to upload job files for admin
export function UploadJobFiles({ isOpen, handleClose, decodedId, notify }) {
  const [jobfiles, setJobfiles] = useState({
    media: { value: [], error: "" },
  });
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [processingMediaCount, setProcessingMediaCount] = useState(0);

  const handleOnClose = () => {
    handleClose();
    setJobfiles({
      media: { value: [], error: "" },
    });
  };

  const handleChange = (fieldName, newFiles) => {
    setJobfiles((prev) => {
      const existingFiles = prev[fieldName]?.value || [];

      // Validate files
      const validatedFiles = [];

      newFiles.forEach((f) => {
        const fileSize = f.file?.size || 0;

        // Image validation
        if (ALLOWED_IMG_TYPES.includes(f.type)) {
          if (fileSize > MAX_FILE_SIZE) {
            notify("warning", `${f.name} cannot exceed 10 MB (image limit)`);
          } else {
            validatedFiles.push(f);
          }
        }

        // Video validation
        else if (ALLOWED_VIDEO_TYPES.includes(f.type)) {
          if (fileSize > MAX_VIDEO_SIZE) {
            notify("warning", `${f.name} cannot exceed 50 MB (video limit)`);
          } else {
            validatedFiles.push(f);
          }
        }

        // PDF validation
        else if (f.type === "application/pdf") {
          if (fileSize > MAX_FILE_SIZE) {
            notify("warning", `${f.name} cannot exceed 10 MB (document limit)`);
          } else {
            validatedFiles.push(f);
          }
        }

        // Unsupported file
        else {
          notify(
            "warning",
            `${f.name} is not allowed. Only JPG, JPEG, PNG, HEIC, HEIF, WEBP images, MP4, MOV, M4V, MKV videos, and PDFs are allowed.`
          );
        }
      });

      // Existing counts
      const imagesCount = existingFiles.filter((f) =>
        f.type?.startsWith("image/")
      ).length;

      const pdfCount = existingFiles.filter(
        (f) => f.type === "application/pdf"
      ).length;

      const videosCount = existingFiles.filter((f) =>
        f.type?.startsWith("video/")
      ).length;

      // Separate validated files
      let newImages = validatedFiles.filter((f) =>
        f.type?.startsWith("image/")
      );

      let newPDFs = validatedFiles.filter(
        (f) => f.type === "application/pdf"
      );

      let newVideos = validatedFiles.filter((f) =>
        f.type?.startsWith("video/")
      );

      // Apply limits
      if (imagesCount + newImages.length > 5) {
        newImages = newImages.slice(0, 5 - imagesCount);
        notify("info", "Cannot add more than 5 images");
      }

      if (pdfCount + newPDFs.length > 5) {
        newPDFs = newPDFs.slice(0, 5 - pdfCount);
        notify("info", "Cannot add more than 5 documents");
      }

      if (videosCount + newVideos.length > 5) {
        newVideos = newVideos.slice(0, 5 - videosCount);
        notify("info", "Cannot add more than 5 videos");
      }

      return {
        ...prev,
        [fieldName]: {
          ...prev[fieldName],
          value: [
            ...existingFiles,
            ...newImages,
            ...newPDFs,
            ...newVideos,
          ],
          error: "",
        },
      };
    });
  };

  const handleSaveClick = async () => {
    setShowLoaderOnClick(true);
    try {
      const formData = new FormData();

      jobfiles.media.value.forEach((item) => {
        if (item.type.startsWith("image/"))
          // key as image as value as file
          formData.append("image", item.file);
        else if (item.type === "application/pdf")
          // key as document and value as file
          formData.append("document", item.file);
        else if (item.type.startsWith("video/"))
          formData.append("video", item.file);
      });

      const res = await uploadJobFilesByID(decodedId, formData);
      if (res.code === 200) {
        notify("success", res?.message || "Files uploaded successfully");
        setTimeout(() => {
          handleOnClose();
        }, 3000);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to add files !";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Failed to add file for this job !");
    } finally {
      setShowLoaderOnClick(false);
    }
  };
  return (
    <>
      <RightPanelCard isOpen={isOpen} onClose={handleOnClose}>
        <Card className="mb-0 service-modal">
          <CardHeader
            className="d-flex align-items-center justify-content-between"
            tag="h4"
          >
            Upload {WORK_ORDER} Files
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleOnClose}
            />
          </CardHeader>
          <hr />
          <Label className="text-muted mb-1 px-3">
            You can upload up to 5 images, 5 documents, and 5 videos.
            <br />Supported formats: JPG, JPEG, PNG, HEIC, HEIF, PDF, MP4, MOV, M4V, and MKV.
            <br />Maximum file size: 10 MB per image/document and 50 MB per video.
          </Label>

          <CardBody className=" scrollbar-inner overflow-auto">
            <Row>
              <Col md="12" className="mb-3 mt-2">
                <FormGroup className="d-flex flex-column align-items-start">
                  <div
                    className="product-image-upload w-100 p-3 border rounded text-center"
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => e.preventDefault()}
                    // onDrop={(e) => {
                    //   e.preventDefault();
                    //   const files = Array.from(e.dataTransfer.files).filter(
                    //     (file) =>
                    //       file.type.startsWith("image/") ||
                    //       file.type.startsWith("video/") ||
                    //       file.type === "application/pdf"
                    //   );

                    //   if (files.length > 0) {
                    //     const newFiles = files.map((file) => ({
                    //       file,
                    //       preview: file.type.startsWith("image/") || file.type.startsWith("video/")
                    //         ? URL.createObjectURL(file)
                    //         : null,
                    //       name: file.name,
                    //       type: file.type,
                    //     }));
                    //     handleChange("media", newFiles);
                    //   }
                    // }}
                    onDrop={async (e) => {
                      e.preventDefault();

                      const files = Array.from(e.dataTransfer.files).filter(
                        (file) =>
                          file.type.startsWith("image/") ||
                          file.type.startsWith("video/") ||
                          file.type === "application/pdf" ||
                          file.name.toLowerCase().endsWith(".heic") ||
                          file.name.toLowerCase().endsWith(".heif")
                      );

                      if (!files.length) return;

                      setProcessingMediaCount((prev) => prev + files.length);

                      const processedFiles = await Promise.all(
                        files.map(async (file) => {
                          try {
                            let finalFile = file;

                            if (
                              file.type.startsWith("image/") ||
                              file.name.toLowerCase().endsWith(".heic") ||
                              file.name.toLowerCase().endsWith(".heif")
                            ) {
                              finalFile = await convertHeicIfNeeded(file);
                            }

                            return {
                              file: finalFile,
                              preview:
                                finalFile.type.startsWith("image/") ||
                                  finalFile.type.startsWith("video/")
                                  ? URL.createObjectURL(finalFile)
                                  : null,
                              name: finalFile.name,
                              type: finalFile.type,
                            };
                          } finally {
                            setProcessingMediaCount((prev) => prev - 1);
                          }
                        })
                      );

                      handleChange("media", processedFiles);
                    }}
                  >
                    {jobfiles.media?.value?.length > 0 ? (
                      <div className="d-flex flex-wrap justify-content-start align-items-center">
                        {jobfiles.media.value.map((item, index) => (
                          <div
                            className="image-preview-wrapper mr-2 position-relative"
                            key={index}
                          >
                            {item.type === "application/pdf" ? (
                              <>
                                <div className="preview-wrapper">
                                  <i className="fa fa-file-pdf text-light fa-4x"></i>
                                </div>
                              </>
                            ) : item.type.startsWith("video/") ? (
                              <video
                                className="product-preview-video"
                                src={item.preview}
                                preload="metadata"
                                autoPlay={false}
                                controls={true}
                              />
                            ) : (
                              <img
                                src={item.preview}
                                alt={`Media ${index}`}
                                className="product-preview-img"
                              />
                            )}

                            <span
                              className="remove-image position-absolute"
                              onClick={() => {
                                setJobfiles((prev) => ({
                                  ...prev,
                                  media: {
                                    ...prev.media,
                                    value: prev.media.value.filter(
                                      (_, i) => i !== index
                                    ),
                                    error: "",
                                  },
                                }));
                              }}
                            >
                              &times;
                            </span>
                            <p
                              className="file-name mt-2 text-truncate"
                              title={item.name}
                            >
                              {item.name}
                            </p>
                          </div>
                        ))}
                        {Array.from({ length: processingMediaCount }).map(
                          (_, index) => (
                            <div
                              key={`processing-${index}`}
                              className="image-preview-wrapper mr-2 position-relative"
                            >
                              <div className="product-preview-img processing-media-tile d-flex justify-content-center align-items-center">
                                <i className="fa fa-spinner fa-spin fa-2x text-muted" />
                              </div>

                              <p className="file-name mt-2 invisible">
                                placeholder
                              </p>
                            </div>
                          ),
                        )}
                        <div
                          className="add-more-container"
                          onClick={() =>
                            document.getElementById("productImageInput").click()
                          }
                        >
                          <div className="add-more-file-wrapper">+</div>
                          <span className="small">Add more</span>
                        </div>
                      </div>
                    ) : (
                      // Show initial instructions when no files uploaded
                      <div className="d-flex flex-column align-items-center">
                        {processingMediaCount > 0 ? (
                          <>
                            <i className="fa fa-spinner fa-spin fa-2x text-muted mb-2" />
                          </>
                        ) : (
                          <>
                            <i className="fa fa-cloud-upload fa-2x text-muted mb-2"></i>
                            <p className="mb-1 text-muted">
                              Click or drag files (Images/Videos/PDFs) to upload
                            </p>
                          </>
                        )}

                        {processingMediaCount === 0 && (
                          <Label
                            for="productImageInput"
                            className="btn btn-simple btn-sm mt-2"
                          >
                            Choose Files
                          </Label>
                        )}

                      </div>
                    )}

                    <Input
                      type="file"
                      name="media"
                      multiple
                      accept="image/*,.heic,.heif,video/*,application/pdf"
                      onChange={async (e) => {
                        const selectedFiles = Array.from(e.target.files).filter(
                          (file) =>
                            file.type.startsWith("image/") ||
                            file.type.startsWith("video/") ||
                            file.type === "application/pdf" ||
                            file.name.toLowerCase().endsWith(".heic") ||
                            file.name.toLowerCase().endsWith(".heif")
                        );

                        if (!selectedFiles.length) {
                          e.target.value = "";
                          return;
                        }

                        setProcessingMediaCount(
                          (prev) => prev + selectedFiles.length
                        );

                        const processedFiles = await Promise.all(
                          selectedFiles.map(async (file) => {
                            try {
                              let finalFile = file;

                              if (
                                file.type.startsWith("image/") ||
                                file.name.toLowerCase().endsWith(".heic") ||
                                file.name.toLowerCase().endsWith(".heif")
                              ) {
                                finalFile = await convertHeicIfNeeded(file);
                              }

                              return {
                                file: finalFile,
                                preview:
                                  finalFile.type.startsWith("image/") ||
                                    finalFile.type.startsWith("video/")
                                    ? URL.createObjectURL(finalFile)
                                    : null,
                                name: finalFile.name,
                                type: finalFile.type,
                              };
                            } finally {
                              setProcessingMediaCount((prev) => prev - 1);
                            }
                          })
                        );

                        if (processedFiles.length > 0) {
                          handleChange("media", processedFiles);
                          // or handleChangeMedia("media", processedFiles);
                          // depending on which function you're using elsewhere
                        }

                        e.target.value = ""; // Reset input
                      }}
                      className="d-none"
                      id="productImageInput"
                    />
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>

          <CardFooter className="d-flex justify-content-end mb-0">
            <Button color="secondary" onClick={handleOnClose} className="mr-2">
              Cancel
            </Button>
            <Button
              color="default"
              type="submit"
              onClick={() => {
                handleSaveClick();
              }}
              disabled={showLoaderOnClick || processingMediaCount > 0}
            >
              {showLoaderOnClick ? (
                <i className="fa fa-spinner fa-spin fa-lg text-white" />
              ) : (
                "Save"
              )}
            </Button>
          </CardFooter>
        </Card>
      </RightPanelCard>
    </>
  );
}
